import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  ShoppingCart,
  LocalShipping,
  History,
  Adjust,
  Close
} from '@mui/icons-material';
import { inventoryService } from '../services/inventoryService';
import { partService } from '../services/partService';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const Inventory = () => {
  const LOW_STOCK_COLS = { partNo: 130, name: 260, onHand: 100, minimum: 100, supplier: 170, category: 140 };
  const TX_COLS = { date: 150, part: 220, type: 120, delta: 90, qtyAfter: 100, notes: 240 };
  const navigate = useNavigate();
  const { isManagerOrAdmin } = useAuth();
  const [tab, setTab] = useState(0);
  const [lowStock, setLowStock] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [parts, setParts] = useState([]);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustPartId, setAdjustPartId] = useState('');
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [txFilters, setTxFilters] = useState({ partId: '', dateFrom: '', dateTo: '', transactionType: '' });
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { colWidths: lowStockColWidths, startResize: startLowStockResize } = useResizableColumns({
    defaultWidths: LOW_STOCK_COLS,
    storageKey: 'wm_inventory_low_stock_col_widths',
    minWidths: { partNo: 100, name: 150, onHand: 80, minimum: 80, supplier: 110, category: 90 }
  });
  const { colWidths: txColWidths, startResize: startTxResize } = useResizableColumns({
    defaultWidths: TX_COLS,
    storageKey: 'wm_inventory_transactions_col_widths',
    minWidths: { date: 120, part: 140, type: 90, delta: 70, qtyAfter: 80, notes: 140 }
  });

  const loadLowStock = async () => {
    try {
      const res = await inventoryService.getLowStock();
      const list = res.data || [];
      setLowStock(list);
      setSelectedPart(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Load low stock:', err);
      setError(err.response?.data?.message || 'Failed to load low stock');
    }
  };

  const loadTransactions = async () => {
    try {
      const params = {
        limit: 50,
        offset: 0,
        ...(txFilters.partId && { partId: txFilters.partId }),
        ...(txFilters.dateFrom && { dateFrom: txFilters.dateFrom }),
        ...(txFilters.dateTo && { dateTo: txFilters.dateTo }),
        ...(txFilters.transactionType && { transactionType: txFilters.transactionType })
      };
      const res = await inventoryService.getTransactions(params);
      const list = res.data || [];
      setTransactions(list);
      setSelectedTransaction(list.length > 0 ? list[0] : null);
      setTransactionsTotal(res.total ?? (res.data?.length ?? 0));
    } catch (err) {
      console.error('Load transactions:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
    }
  };

  const loadParts = async () => {
    try {
      const res = await partService.getAll({ limit: 500, activeOnly: true });
      setParts(res.data || []);
    } catch (err) {
      console.error('Load parts:', err);
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      await Promise.all([loadLowStock(), loadParts()]);
      setLoading(false);
    };
    run();
  }, []);

  useEffect(() => {
    if (tab === 1) loadTransactions();
  }, [tab, txFilters.partId, txFilters.dateFrom, txFilters.dateTo, txFilters.transactionType]);

  const handleAdjustSubmit = async () => {
    const partId = parseInt(adjustPartId, 10);
    const delta = parseFloat(adjustDelta);
    if (!partId || isNaN(delta) || delta === 0) {
      setError('Select a part and enter a non-zero quantity delta.');
      return;
    }
    setAdjustSaving(true);
    setError('');
    try {
      await inventoryService.createAdjustment(partId, delta, adjustReason, adjustNotes);
      setAdjustOpen(false);
      setAdjustPartId('');
      setAdjustDelta('');
      setAdjustReason('');
      setAdjustNotes('');
      await loadLowStock();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply adjustment');
    } finally {
      setAdjustSaving(false);
    }
  };

  const canWrite = isManagerOrAdmin && typeof isManagerOrAdmin === 'function' && isManagerOrAdmin();

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
        Inventory
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Selected Item Information Bar */}
      <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {tab === 0 ? (
            <>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Part #</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedPart?.part_number ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Name</Typography>
                <Typography variant="body2">{selectedPart?.name ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>On Hand</Typography>
                <Typography variant="body2">{selectedPart?.quantity_on_hand ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Minimum</Typography>
                <Typography variant="body2">{selectedPart?.minimum_quantity ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Supplier</Typography>
                <Typography variant="body2">{selectedPart?.supplier ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Category</Typography>
                <Typography variant="body2">{selectedPart?.category ?? '—'}</Typography>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTransaction?.created_at ? format(new Date(selectedTransaction.created_at), 'yyyy-MM-dd HH:mm') : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Part</Typography>
                <Typography variant="body2">{selectedTransaction ? `${selectedTransaction.part_number ?? ''} – ${selectedTransaction.part_name ?? ''}` : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Type</Typography>
                <Typography variant="body2">{selectedTransaction?.transaction_type ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Delta</Typography>
                <Typography variant="body2">{selectedTransaction?.quantity_delta != null ? (selectedTransaction.quantity_delta > 0 ? `+${selectedTransaction.quantity_delta}` : selectedTransaction.quantity_delta) : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Qty After</Typography>
                <Typography variant="body2">{selectedTransaction?.quantity_after ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Notes</Typography>
                <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedTransaction?.notes ?? '—'}</Typography>
              </Box>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            size="small"
            onClick={() => { setSelectedPart(null); setSelectedTransaction(null); }}
            disabled={!(selectedPart || selectedTransaction)}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Low stock parts</Typography>
              <Typography variant="h4">{loading ? '—' : lowStock.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/parts')}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Parts catalog</Typography>
              <Typography variant="body2">View & edit parts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/procurement/shipments-in')}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Receiving</Typography>
              <Typography variant="body2">Receive & put away</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              {canWrite && (
                <Button
                  size="small"
                  startIcon={<Adjust />}
                  onClick={() => { setAdjustOpen(true); setError(''); }}
                >
                  Adjust stock
                </Button>
              )}
              {!canWrite && (
                <Typography variant="body2" color="textSecondary">Adjust stock (manager/admin)</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedPart(null); setSelectedTransaction(null); }}>
          <Tab label="Low stock" />
          <Tab label="Movement history" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <>
              {loading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <ResizableHeaderCell width={lowStockColWidths.partNo} onResizeStart={(e) => startLowStockResize('partNo', e)}>Part #</ResizableHeaderCell>
                        <ResizableHeaderCell width={lowStockColWidths.name} onResizeStart={(e) => startLowStockResize('name', e)}>Name</ResizableHeaderCell>
                        <ResizableHeaderCell width={lowStockColWidths.onHand} onResizeStart={(e) => startLowStockResize('onHand', e)} align="right">On hand</ResizableHeaderCell>
                        <ResizableHeaderCell width={lowStockColWidths.minimum} onResizeStart={(e) => startLowStockResize('minimum', e)} align="right">Minimum</ResizableHeaderCell>
                        <ResizableHeaderCell width={lowStockColWidths.supplier} onResizeStart={(e) => startLowStockResize('supplier', e)}>Supplier</ResizableHeaderCell>
                        <ResizableHeaderCell width={lowStockColWidths.category} onResizeStart={(e) => startLowStockResize('category', e)}>Category</ResizableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lowStock.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center">No low stock parts</TableCell></TableRow>
                      ) : (
                        lowStock.map((row) => (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: selectedPart?.id === row.id ? 'action.selected' : 'inherit'
                            }}
                            onClick={() => setSelectedPart(row)}
                            onDoubleClick={() => navigate('/parts')}
                          >
                            <TableCell sx={{ width: lowStockColWidths.partNo }}>{row.part_number}</TableCell>
                            <TableCell sx={{ width: lowStockColWidths.name }}>{row.part_description ?? row.description ?? '—'}</TableCell>
                            <TableCell sx={{ width: lowStockColWidths.onHand }} align="right">{row.quantity_on_hand ?? '—'}</TableCell>
                            <TableCell sx={{ width: lowStockColWidths.minimum }} align="right">{row.minimum_quantity ?? '—'}</TableCell>
                            <TableCell sx={{ width: lowStockColWidths.supplier }}>{row.supplier ?? '—'}</TableCell>
                            <TableCell sx={{ width: lowStockColWidths.category }}>{row.category ?? '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {tab === 1 && (
            <>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Part</InputLabel>
                    <Select
                      value={txFilters.partId}
                      label="Part"
                      onChange={(e) => setTxFilters((f) => ({ ...f, partId: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {parts.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>{p.part_number} – {p.part_description ?? p.description}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <TextField
                    size="small"
                    label="From date"
                    type="date"
                    value={txFilters.dateFrom}
                    onChange={(e) => setTxFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    size="small"
                    label="To date"
                    type="date"
                    value={txFilters.dateTo}
                    onChange={(e) => setTxFilters((f) => ({ ...f, dateTo: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={txFilters.transactionType}
                      label="Type"
                      onChange={(e) => setTxFilters((f) => ({ ...f, transactionType: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="receive">Receive</MenuItem>
                      <MenuItem value="consume">Consume</MenuItem>
                      <MenuItem value="adjust_in">Adjust in</MenuItem>
                      <MenuItem value="adjust_out">Adjust out</MenuItem>
                      <MenuItem value="return">Return</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <ResizableHeaderCell width={txColWidths.date} onResizeStart={(e) => startTxResize('date', e)}>Date</ResizableHeaderCell>
                      <ResizableHeaderCell width={txColWidths.part} onResizeStart={(e) => startTxResize('part', e)}>Part</ResizableHeaderCell>
                      <ResizableHeaderCell width={txColWidths.type} onResizeStart={(e) => startTxResize('type', e)}>Type</ResizableHeaderCell>
                      <ResizableHeaderCell width={txColWidths.delta} onResizeStart={(e) => startTxResize('delta', e)} align="right">Delta</ResizableHeaderCell>
                      <ResizableHeaderCell width={txColWidths.qtyAfter} onResizeStart={(e) => startTxResize('qtyAfter', e)} align="right">Qty after</ResizableHeaderCell>
                      <ResizableHeaderCell width={txColWidths.notes} onResizeStart={(e) => startTxResize('notes', e)}>Notes</ResizableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No transactions</TableCell></TableRow>
                    ) : (
                      transactions.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: selectedTransaction?.id === row.id ? 'action.selected' : 'inherit'
                          }}
                          onClick={() => setSelectedTransaction(row)}
                        >
                          <TableCell sx={{ width: txColWidths.date }}>{row.created_at ? format(new Date(row.created_at), 'yyyy-MM-dd HH:mm') : '—'}</TableCell>
                          <TableCell sx={{ width: txColWidths.part }}>{row.part_number} – {row.part_name}</TableCell>
                          <TableCell sx={{ width: txColWidths.type }}>{row.transaction_type}</TableCell>
                          <TableCell sx={{ width: txColWidths.delta }} align="right">{row.quantity_delta > 0 ? `+${row.quantity_delta}` : row.quantity_delta}</TableCell>
                          <TableCell sx={{ width: txColWidths.qtyAfter }} align="right">{row.quantity_after ?? '—'}</TableCell>
                          <TableCell sx={{ width: txColWidths.notes, maxWidth: 200 }}>{row.notes ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Paper>

      <Dialog open={adjustOpen} onClose={() => !adjustSaving && setAdjustOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust stock</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
            <InputLabel>Part</InputLabel>
            <Select
              value={adjustPartId}
              label="Part"
              onChange={(e) => setAdjustPartId(e.target.value)}
            >
              <MenuItem value="">Select part</MenuItem>
              {parts.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{p.part_number} – {p.name} (on hand: {p.quantity_on_hand ?? 0})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Quantity delta"
            type="number"
            value={adjustDelta}
            onChange={(e) => setAdjustDelta(e.target.value)}
            placeholder="Positive to add, negative to subtract"
            sx={{ mt: 1, mb: 1 }}
            InputProps={{ inputProps: { step: 0.01 } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Reason"
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
            sx={{ mt: 1, mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Notes"
            value={adjustNotes}
            onChange={(e) => setAdjustNotes(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustOpen(false)} disabled={adjustSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleAdjustSubmit} disabled={adjustSaving}>
            {adjustSaving ? <CircularProgress size={24} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
